import ReactDOM from 'react-dom';
import { useEffect, useRef } from 'react';
import useModalStore from '../../../zustand/store/useModalStore';
import { useShallow } from 'zustand/shallow';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import { useSubIcon } from '../../../hooks/useSubIcon';
import { FocusTrap } from 'focus-trap-react';

const ConfirmModal = () => {
    
    // Dynamic Style Hook
    const { primaryBackColor } = useDynamicStyle();
    
    // icon Hook
    const { NotiIcon } = useSubIcon();
    
    // modal Hook
    const { notiModal, closeNotiModal, confirmModal, closeConfirmModal, modalMessage, modalOptions, promptModal, closePromptModal, promptValue, setPromptValue } =
        useModalStore(useShallow(
            state => ({
                notiModal: state.notiModal,
                closeNotiModal: state.closeNotiModal,
                confirmModal: state.confirmModal,
                closeConfirmModal: state.closeConfirmModal,
                modalMessage: state.modalMessage,
                modalOptions: state.modalOptions,
                promptModal: state.promptModal,
                closePromptModal: state.closePromptModal,
                promptValue: state.promptValue,
                setPromptValue: state.setPromptValue,
            })
        ));

    // 필요한 확인 text를 추가하지 않을 경우 '확인'과 '닫기'가 default
    const okText = (modalOptions && modalOptions.okText) || '확인';
    const cancelText = (modalOptions && modalOptions.cancelText) || '닫기';
    
    useEffect(() => {
        // modal이 전부 닫혀있으면 렌더링 안함
        if (!notiModal && !confirmModal && !promptModal) return;
        
        const handleKeyDown = (e) => {
            // 현재 포커스된 요소가 '닫기' 일 때 처리
            if(e.target.classList.contains('btn_close')) {
                if(e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    closeConfirmModal(false);
                    closePromptModal(false);
                    closeNotiModal();
                    return;
                }
            }

            // enter event
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();

                if (confirmModal) {
                    closeConfirmModal(true);
                } else if (promptModal) {
                    e.preventDefault();
                    if (promptValue?.trim()) {
                        closePromptModal(true);
                    }
                } else {
                    closeNotiModal();
                }
            }
            // esc event
            if (e.key === 'Escape') {
                if (confirmModal) {
                    closeConfirmModal(false);
                } else if (promptModal) {
                    e.preventDefault();
                    closePromptModal(false);
                } else {
                    closeNotiModal();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);

        return () => window.removeEventListener('keydown', handleKeyDown, true);

    }, [confirmModal, notiModal, promptModal, promptValue]);

    const promptInputRef = useRef();
    const modalRef = useRef();

    useEffect(() => {
        if (promptModal) {
            setTimeout(() => {
                promptInputRef.current?.focus();
            }, 0);
        }
    }, [promptModal]);

    const modalContent = (
        <FocusTrap focusTrapOptions={{ initialFocus: '.modal_container', fallbackFocus: '.modal_container' }}>
            <div className='modal_container modal_top_z' ref={modalRef} tabIndex={-1}>
                <div className='confirm_wrapper'>
                    <div className='modal_title_box'>
                        {NotiIcon}
                        <h4 className='text-center'>
                            {modalMessage.split('\n').map((line, idx) => (
                                <span key={idx}>
                                    {line}
                                    <br />
                                </span>
                            ))}
                        </h4>
                    </div>
                    {confirmModal && 
                        <div className='modal_btn_box'>
                            <button
                                className={`btn_confirm modal_btn ${primaryBackColor}`}
                                onClick={() => closeConfirmModal(true)} // 확인 -> resolve(true)
                            > {okText}
                            </button>
                            <button
                                className={`btn_close modal_btn bg_co_black hover_co_dark_bg`}
                                onClick={() => closeConfirmModal(false)} // 취소 -> resolve(false)
                            > {cancelText}
                            </button>
                        </div>
                    }
                    {notiModal &&
                        <button
                            className={`btn_close modal_btn w-full ${primaryBackColor}`}
                            onClick={() => closeNotiModal()}
                        > {okText}
                        </button>
                    }
                    {promptModal &&
                        <>
                            <input
                                type={modalOptions?.type ?? 'text'}
                                value={promptValue}
                                onChange={(e) => setPromptValue(e.target.value)}
                                placeholder={modalOptions?.placeholder ?? ''}
                                className='input_style w-64'
                                ref={promptInputRef}
                            />
                            <div className='modal_btn_box'>
                                <button
                                    className={`modal_btn ${primaryBackColor}`}
                                    onClick={() => closePromptModal(true)}
                                    disabled={!promptValue?.trim()}   // 빈값 방지
                                > {okText}
                                </button>
                                <button
                                    className='btn_close modal_btn bg_co_black hover_co_dark_bg'
                                    onClick={() => closePromptModal(false)}
                                > {cancelText}
                                </button>
                            </div>
                        </>
                    }
                </div>
            </div>
        </FocusTrap>
    );
    
    return ReactDOM.createPortal(
        modalContent, 
        document.getElementById('modal-root')
    );
}
export default ConfirmModal;